<template>
    <div>
        <md-dialog md-open-from="#insertPage" md-close-to="#insertPage" ref="insertPage" @open="fixContent" @close="startOver">
            <div style="text-align: center;">
                <div style="padding: 14px;padding-bottom: 0px;" >
                    <md-dialog-content>
                        <p>Insert Page</p>
                        <md-input-container>
                            <label for="insert-page-file">File</label>
                            <md-file id="insert-page-file" :key="fileInputKey" required @selected="onAddPageFileChange" accept="application/pdf"></md-file>
                        </md-input-container>
                        <p v-if="addErrorFromMessage" class="error">{{ addErrorFromMessage }}</p>
                        <div class="from-to">
                                <md-radio v-model="add_page_where" mdValue="before">Before</md-radio>
                                <md-radio v-model="add_page_where" mdValue="after">After</md-radio>
                        </div>
                        <md-button class="md-primary md-raised" style="margin-bottom: 25px;" @click="addPages" :disabled="loading">Add Page(s)</md-button>      
                    </md-dialog-content>
                </div>    
            </div>
        </md-dialog>
        <md-dialog ref="savedDraftThumbnailDialog" @close="savedDraftThumbnailDialogCloseOutside">
            <md-dialog-content style="padding: 24px; width: 800px;">
                <p style="margin-bottom: 16px;">Warning, you are about to change the PDF for all users. This cannot be undone.</p>
                <p style="font-size: 16px; font-weight: bold;">
                    Cuelist recommends creating a backup before applying any changes.
                </p>
                <p v-if="project.autoSaveCap === true" style="margin-top: 12px;">
                    Cuelist keeps your last 10 page-change saves.
                </p>
            </md-dialog-content>

            <md-dialog-actions style="display: flex; justify-content: flex-end; padding: 16px;">
                <md-button class="md-default" @click="savedDraftThumbnailDialogClose">
                    Cancel
                </md-button>
                <md-button class="md-accent" @click="onDialogClose('proceedWithoutSaving')">
                    Proceed Without Saving
                </md-button>
                <md-button class="md-primary md-raised" @click="onDialogClose('saveAndContinue')">
                    Save & Continue
                </md-button>
            </md-dialog-actions>
        </md-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBus from '@/buses/nav';
import { pageOpDetail } from '@/lib/history-autosave';
import PDFJS from 'pdfjs-dist/webpack';
import {PROJECT_ADD_PAGES_URL} from '@/lib/URLs';
import { apiAuthHeaders } from '@/lib/api-auth-client';
import uploadPageFile from '@/lib/page-upload-client';
import { pdfTooLargeMessage, pageUploadFailed, PAGE_UPLOAD_FAILED_MESSAGE } from '@/lib/page-upload';
import { useServerPageChange } from '@/lib/server-page-change';
import pageChangeLockMode from '@/lib/page-change-lock-mode';
import { pageCountForPageOp } from '@/lib/page-labels';

const CreateProject = {
    name: 'insertPage',
    // Which page-change lock this window uses (lib/page-change-lock-mode.js, shipped dark 20 Oct 2026).
    mixins: [pageChangeLockMode],
    props: [
        'currentPage',
        'add_page_into'
    ],
    data: function() {
        return {
            addPageFile: null,
            addPageFilePagesCount: 0,
            add_page_where: 'after',
            posterImage: null,
            validPoster: false,
            uploadingPoster: false,
            projectCuePlacement: this.$props.cuePlacement || 'right',
            addErrorFromMessage: '',
            loading: false,
            fileInputKey: Date.now(),
            isSavedDraftDialogOpen: false,
            pageChangeRunning: false,
            lockStaysUp: false
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'users',
            'project',
            'cues',
            'notes',
            'bookmarks',
            'currentVersion',
            'projectTotalPages',
            'projectID'
        ]),
        isCreateDisabled() {
            return !(this.project.title && this.project.file && this.project.ownerRole);
        },
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        // Labels and the loaded PDF must agree before the helper rebuilds the PDF
        // (7 Oct 2026, lib/page-labels.js): a short label list used to cut pages off.
        pageOpCheck() {
            return pageCountForPageOp({ labels: this.project.pdfPageNumbers, loadedPageCount: this.projectTotalPages });
        },
        totalPages: function() {
            if (this.versionID) {
                return this.projectTotalPages || 0;
            }
            
            return (this.project && Array.isArray(this.project.pdfPageNumbers)) 
                ? this.project.pdfPageNumbers.length 
                : 0;
        }
    },
    methods: {
        addValidateNumber(e) {
            if (e < 1) {
                this.addErrorFromMessage = 'Value should be greater than or equal to 1.';
            } else if (e > this.totalPages) {
                this.addErrorFromMessage = `Value should be less than or equal to ${this.totalPages}.`;
            } else {
                this.addErrorFromMessage = '';
            }
        },
        async open() {
            this.addPageFile = null;
            this.addErrorFromMessage = '';
            this.fileInputKey = Date.now();
            await this.$refs.savedDraftThumbnailDialog.close();
            await this.beginPageChangeWindow();
            await this.$refs.insertPage.open();
        },
        addPages() {
            if (this.refuseIfPagesOutOfStep()) {
                return;
            }
            if (!this.addPageFile) {
                this.addErrorFromMessage = 'Oops! You need to select a PDF first.';
            } else {
                this.cancel();
                this.$nextTick(async () => {
                    this.beginPageChangeWindow();
                    this.isSavedDraftDialogOpen = true;
                    await this.$refs.savedDraftThumbnailDialog.open();
                });
            }
        },
        async savedDraftThumbnailDialogClose() {
            this.isSavedDraftDialogOpen = false;
            await this.endPageChangeWindow();
            await this.$refs.savedDraftThumbnailDialog.close();
        },
        async savedDraftThumbnailDialogCloseOutside() {
            this.isSavedDraftDialogOpen = false;
            await this.endPageChangeWindow();
        },
        // Josh, 14 Oct: the lock goes on at the click, not when the window opens (lib/page-change-lock.js).
        // true = this person holds it and the change may start. 'held': someone else is changing pages and
        // their hold message shows here; anything else is an error and nothing starts.
        async lockForPageChange() {
            this.pageChangeRunning = true;
            this.lockStaysUp = false;
            this.$store.dispatch('setLoader', { type: true });
            let result;
            try {
                result = await this.$store.dispatch('takePageChangeLock');
            } catch (error) {
                console.error('[page-change] could not set the lock:', error);
                result = 'error';
            }
            if (result === 'taken') return true;
            this.pageChangeRunning = false;
            this.$store.dispatch('setLoader', { type: false });
            if (result === 'held') {
                await this.$refs.savedDraftThumbnailDialog.close();
            } else if (this.$toast) {
                // Josh's wording, 14 Oct: the lock could not be set, so nothing started.
                this.$toast.error("The page change didn't start. Nothing was changed. Please try again.");
            }
            return false;
        },
        releaseLockAfterChange() {
            this.pageChangeRunning = false;
            if (this.lockStaysUp) return Promise.resolve('kept');
            return this.$store.dispatch('releasePageChangeLock');
        },
        refuseIfPagesOutOfStep() {
            if (this.pageOpCheck.ok) {
                return false;
            }
            console.error('[page-op] refused:', this.pageOpCheck.reason);
            if (this.$toast) {
                this.$toast.error("This script's page numbering is out of step with its pages, so nothing was changed. Refresh your browser and try again. If this keeps happening, contact support.");
            }
            return true;
        },
        // The page's label ("12A"), for the History row's detail line.
        pageLabel(pageNumber) {
            const page = (this.project.pdfPageNumbers || [])[Number(pageNumber) - 1];
            return page && page.name !== undefined ? page.name : pageNumber;
        },
        getFormattedVersionTitle() {
            const now = new Date();
            const date = now.toLocaleDateString('en-GB');
            const time = now.toLocaleTimeString('en-US', { hour12: true });

            const formattedDate = date.replaceAll('/', '-');
            return `PDF Adjustment – ${formattedDate} ${time}`;
        },
        async onDialogClose(type) {
            this.isSavedDraftDialogOpen = false;
            if (type === 'proceedWithoutSaving') {
                if (this.addPageFile && this.addPageFilePagesCount) {
                    if (!(await this.startPageChange())) return;
                    this.working = true;
                    await this.doUploadFileToAddPages();
                    await this.finishPageChange();
                }
            } else if (type === 'saveAndContinue') {
                if (this.addPageFile && this.addPageFilePagesCount) {
                    if (!(await this.startPageChange())) return;
                    this.working = true;
                    try {
                        const versionTitle = this.getFormattedVersionTitle();
                        await this.$store.dispatch('createVersion', {
                            name: versionTitle,
                            autoSave: 'page_op',
                            autoSaveDetail: pageOpDetail({ op: 'insert', count: this.addPageFilePagesCount, where: this.add_page_where, at: this.pageLabel(this.add_page_into) })
                        });
                        await this.$store.dispatch('pruneAutoSaves', { kind: 'page_op' });
                        await this.doUploadFileToAddPages();
                        await this.finishPageChange();
                    } catch (error) {
                        console.error('Error saving version:', error);
                        this.afterFailedSave();
                        this.$store.dispatch('setLoader', { type: false });
                        this.working = false;
                        this.loading = false;
                        this.$toast.error('Failed to save version. Please try again.');
                    }
                }
            }
        },
        getNextAlphaSuffix(currentSuffix) {
            const arr = currentSuffix.split('');
            let i = arr.length - 1;

            while (i >= 0) {
                if (arr[i] !== 'z') {
                    arr[i] = String.fromCodePoint(arr[i].codePointAt(0) + 1);
                    return arr.join('');
                }
                arr[i] = 'a';
                i--;
            }

            return 'a' + arr.join('');
        },
        shouldUpdateCuePageNumber(cue) {
            if (cue.pageNumber === this.add_page_into) {
                return false;
            }

            if (this.add_page_where === 'before') {
                return this.add_page_into === 1 || cue.pageNumber > this.add_page_into;
            }

            return this.add_page_where === 'after' && cue.pageNumber > this.add_page_into;
        },
        shouldUpdateNotePageNumber(note) {
            if (note.page === this.add_page_into) {
                return false;
            }

            if (this.add_page_where === 'before') {
                return this.add_page_into === 1 || note.page > this.add_page_into;
            }

            return this.add_page_where === 'after' && note.page > this.add_page_into;
        },
        shouldUpdateBookmarkPageNumber(bookmarkPage) {
            if (Number(bookmarkPage) === this.add_page_into) {
                return false;
            }

            if (this.add_page_where === 'before') {
                return this.add_page_into === 1 || Number(bookmarkPage) > this.add_page_into;
            }

            return this.add_page_where === 'after' && Number(bookmarkPage) > this.add_page_into;
        },
        async doUploadFileToAddPages() {
            this.loading = true;
            const regexp = /^https?:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)\?alt=media&token=[^&]+$/;
            const matches = regexp.exec(this.project.documentUrl);

            if (!matches || !matches[1]) {
                this.$toast.error('Invalid document URL');
                return;
            }

            try {
                const pageOp = this.pageOpCheck;
                if (!pageOp.ok) throw new Error('Page op refused: ' + pageOp.reason);
                this.working = true;
                this.$store.dispatch('setLoader', { type: true });

                const formData = new FormData();
                formData.append('project_id', this.project.id);
                formData.append('documentUrl', this.project.documentUrl);
                formData.append('filePath', decodeURIComponent(matches[1]));
                formData.append('add_page_into', this.add_page_into);
                formData.append('add_page_where', this.add_page_where);
                formData.append('totalPages', pageOp.total);
                // The PDF goes to Storage first; the helper is sent only its path (big PDFs, 26 Oct 2026).
                formData.append('uploadPath', await uploadPageFile(this.project.id, this.addPageFile));

                let response;
                try {
                    response = await fetch(PROJECT_ADD_PAGES_URL, {
                        method: 'POST',
                        headers: await apiAuthHeaders(PROJECT_ADD_PAGES_URL),
                        body: formData
                    });
                } catch (e) {
                    throw pageUploadFailed(e);
                }
                if (response.status === 413) throw pageUploadFailed(new Error('413 from the helper'));

                if (!response.ok) throw new Error('Failed to upload file');

                // Shows switched to the server renumber (lib/server-page-change.js): one call instead of the writes below.
                if (this.addPageFilePagesCount > 0 && useServerPageChange(this.project)) {
                    await this.finishInsertOnServer(pageOp.total);
                } else if (this.addPageFilePagesCount > 0 && this.project.pdfPageNumbers.length > 0) {
                    this.$store.dispatch('updateShowThumbnailPageUpdate', true);
                    const pdfPages = [...this.project.pdfPageNumbers];
                    const lastSelectedIndex = this.add_page_into - 1;
                    let prefix;

                    if (lastSelectedIndex > 0) {
                        prefix = this.add_page_where === 'before' ? pdfPages[lastSelectedIndex - 1].name : pdfPages[lastSelectedIndex].name;
                    } else {
                        prefix = this.add_page_where === 'before' ? '0' : pdfPages[lastSelectedIndex].name;
                    }

                    let currentSuffix = 'a';
                    const newPages = Array.from({ length: this.addPageFilePagesCount }, (_, i) => {
                        const pageId = this.add_page_where === 'before' ? lastSelectedIndex - i : this.add_page_into + i;
                        const name = `${prefix}${currentSuffix}`;
                        currentSuffix = this.getNextAlphaSuffix(currentSuffix);
                        return { id: pageId, name };
                    });

                    const insertIndex = this.add_page_where === 'before' ? lastSelectedIndex : this.add_page_into;
                    pdfPages.splice(insertIndex, 0, ...newPages);

                    const updatedPdfPages = pdfPages.map((item, index) => ({ ...item, id: index }));

                    await this.$store.dispatch('renamePdfPages', { pdfPage: updatedPdfPages });

                    const pageOffset = this.addPageFilePagesCount;

                    const cuesToUpdate = [];
                    this.cues.forEach((cue) => {
                        if (this.add_page_where === 'before') {
                            if (this.add_page_into === 1) {
                                cue.pageNumber += pageOffset;
                                cuesToUpdate.push(cue);
                            } else if (cue.pageNumber >= this.add_page_into) {
                                cue.pageNumber += pageOffset;
                                cuesToUpdate.push(cue);
                            }
                        } else if (this.add_page_where === 'after' && this.add_page_into !== cue.pageNumber && cue.pageNumber > this.add_page_into) {
                            cue.pageNumber += pageOffset;
                            cuesToUpdate.push(cue);
                        }
                    });
                    // Batch the cue pageNumber shifts (was ~1,000+ individual writes on
                    // dense projects). bulkUpdateCues chunks them into ~490-op batches.
                    const updateCuePromises = [this.$store.dispatch('bulkUpdateCues', cuesToUpdate)];

                    const updateNotePromises = this.notes
                        .map((note) => {
                            if (this.add_page_where === 'before') {
                                if (this.add_page_into === 1) {
                                    note.page += pageOffset;
                                    return this.$store.dispatch('updateUserNote', { note });
                                }
                                if (note.page >= this.add_page_into) {
                                    note.page += pageOffset;
                                    return this.$store.dispatch('updateUserNote', { note });
                                }
                                return null;
                            }
                            if (this.add_page_where === 'after' && this.add_page_into !== note.page && note.page > this.add_page_into) {
                                note.page += pageOffset;
                                return this.$store.dispatch('updateUserNote', { note });
                            }
                            return null;
                        });

                    // Bookmarks — collect the affected pages and shift them in one
                    // atomic batch (see moveBookmarksBulk). Comparisons use Number()
                    // because bookmark keys are strings; the old code string-concatted
                    // the key (`"3" + 1`) when inserting before page 1, corrupting it.
                    const into = Number(this.add_page_into);
                    const bookmarkMoves = [];
                    Object.keys(this.bookmarks).forEach((bookmarkPage) => {
                        const pageNum = Number(bookmarkPage);
                        let shift = false;
                        if (this.add_page_where === 'before') {
                            shift = into === 1 || pageNum >= into;
                        } else if (this.add_page_where === 'after') {
                            shift = pageNum > into;
                        }
                        if (shift) {
                            bookmarkMoves.push({ from: bookmarkPage, to: pageNum + pageOffset });
                        }
                    });
                    const updateBookmarkPromise = this.$store.dispatch('moveBookmarksBulk', { moves: bookmarkMoves });

                    // Act/Scene/Song markers key off `pageIndex`, not `pageNumber`, so they
                    // need their own shift — without it structure stays on the old numeric
                    // page while every cue, note and bookmark moves past it. Mirrors the
                    // project-settings nav insert path and delete-page.vue.
                    const shiftFromPage = this.add_page_where === 'before'
                        ? Number(this.add_page_into)
                        : Number(this.add_page_into) + 1;
                    const shiftMarkersPromise = this.$store.dispatch('shiftScriptMarkersAfterPage', {
                        fromPage: shiftFromPage,
                        delta: pageOffset
                    });

                    await Promise.all([
                        ...updateCuePromises,
                        ...updateNotePromises,
                        updateBookmarkPromise,
                        shiftMarkersPromise
                    ]);
                    this.$store.dispatch('notifyNotesListStructureIfLinked');

                    const newPagePosition = Number(this.add_page_into) + (this.add_page_where === 'after' ? 1 : 0);
                    NavBus.$emit('reloadCurrentProject', { newPagePosition });

                    this.addPageFile = null;
                    this.addPageFilePagesCount = 0;
                    this.working = false;
                    this.$store.dispatch('setLoader', { type: false });
                    this.loading = false;
                    this.$store.dispatch('updateShowThumbnailPageUpdate', false);
                }
                // The reload above can already have removed this window; closing it then threw into the catch below.
                if (this.$refs.insertPage) this.$refs.insertPage.close();
            } catch (error) {
                console.error(error);
                // A server change that stopped part way after every retry keeps the lock up (Josh, 14 Oct).
                if (error && error.stoppedPartWay) this.lockStaysUp = true;
                this.working = false;
                this.$store.dispatch('setLoader', { type: false });
                this.loading = false;
                if (this.$toast) this.$toast.error(error && error.pageUploadFailed ? PAGE_UPLOAD_FAILED_MESSAGE : 'Something went wrong updating the pages. Please reload and check the affected pages.');
            }
        },
        async finishInsertOnServer(pagesBefore) {
            await this.$store.dispatch('applyPageChangeOnServer', {
                op: 'insert',
                params: { into: Number(this.add_page_into), where: this.add_page_where, count: this.addPageFilePagesCount },
                pagesBefore
            });
            // Only after the server returns (14 Oct 2026; see delete-page.vue finishDeleteOnServer).
            this.$store.dispatch('updateShowThumbnailPageUpdate', true);
            const newPagePosition = Number(this.add_page_into) + (this.add_page_where === 'after' ? 1 : 0);
            NavBus.$emit('reloadCurrentProject', { newPagePosition });
            this.addPageFile = null;
            this.addPageFilePagesCount = 0;
            this.working = false;
            this.$store.dispatch('setLoader', { type: false });
            this.loading = false;
            this.$store.dispatch('updateShowThumbnailPageUpdate', false);
        },
        onAddPageFileChange: async function(fileList) {
            this.addPageFile = fileList[0];
            const tooLarge = this.addPageFile && pdfTooLargeMessage(this.addPageFile.size);
            if (tooLarge) {
                this.addPageFile = null;
                this.addPageFilePagesCount = 0;
                this.fileInputKey = Date.now();
                if (this.$toast) this.$toast.error(tooLarge);
                return;
            }
            if (this.addPageFile) {
                const arrayBuffer = await this.addPageFile.arrayBuffer();
                PDFJS.getDocument({data: new Uint8Array(arrayBuffer)}).then((pdf) => {
                    this.addPageFilePagesCount = pdf.pdfInfo.numPages;
                });
                this.addErrorFromMessage = '';
            }
        },
        fixContent() {
            if (this.$refs.stepper) {
                this.$nextTick(() => {
                    this.$refs.stepper.calculateStepsWidthAndPosition();
                });
            }
        },
        startOver() {
            setTimeout(async () => {
                if (!this.isSavedDraftDialogOpen) {
                    await this.endPageChangeWindow();
                }
            }, 1200);
        },
        cancel() {
            this.$refs.insertPage.close();
        }
    },
    components: {
    }
};
export default CreateProject;
</script>

<style>
.md-dialog {
    min-width: 400px;
}
.md-dialog-content h2 {
  font-size: 22px;
  margin-bottom: 12px;
}

.md-dialog-content p {
  font-size: 16px;
}

.md-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.md-button + .md-button {
  margin-left: 8px;
}

</style>


// WEBPACK FOOTER //
// insert-page.vue?22655fe2